import { cn } from '@forest/shared'
import { createSignal, Show } from 'solid-js'
import bg from '~/assets/countdown-bg.webp'
import { CloseIcon } from '~/components/icon/close'
import { useCountdown } from '~/hooks/use-countdown'
import { native } from '@forest/native-services'
import { m } from '~/paraglide/messages'
import { useTimestamp } from '~/resources/timestamp'

type Props = {
  startDate: Date
  treeImg: string
}

const pad = (n: number) => String(Math.max(0, n)).padStart(2, '0')

export function CountdownDialog(props: Props) {
  const timestamp = useTimestamp()
  const countdown = useCountdown(() => ({
    now: timestamp(),
    endDate: props.startDate,
    includesDay: true,
  }))
  const isOpen = () => timestamp().getTime() < props.startDate.getTime()
  const [isBgLoaded, setIsBgLoaded] = createSignal(false)
  const [isTreeImgLoaded, setIsTreeImgLoaded] = createSignal(false)

  return (
    <Show when={isOpen()}>
      <div
        class="fixed inset-0 z-50 overflow-hidden"
        style={{
          background:
            'linear-gradient(180deg, #003336 73.664%, #00939C 131.9%)',
        }}
      >
        {/* Background image with fade mask */}
        <div class="absolute left-1/2 -translate-x-1/2">
          <img
            src={bg}
            alt=""
            class={cn(
              'pointer-events-none absolute left-[-970px] top-[-42px] min-h-[594px] min-w-[1578px] opacity-0 transition-opacity',
              isBgLoaded() && 'opacity-100',
            )}
            onLoad={() => {
              setIsBgLoaded(true)
            }}
          />
        </div>

        {/* Close button */}
        <button
          class="absolute left-4 top-[calc(16px+var(--safe-area-top))] z-10 grid size-10 place-items-center rounded-full bg-white/80 backdrop-blur-sm"
          onClick={() => native.general.close()}
        >
          <CloseIcon />
        </button>

        {/* Tree image */}
        <img
          src={props.treeImg}
          alt=""
          class={cn(
            'absolute left-1/2 top-[121px] size-[221px] -translate-x-1/2 object-contain opacity-0 transition-opacity',
            isTreeImgLoaded() && 'opacity-100',
          )}
          onLoad={() => {
            setIsTreeImgLoaded(true)
          }}
        />

        {/* Event content */}
        <div class="absolute inset-x-6 top-[calc(50%+133.5px)] flex -translate-y-1/2 flex-col items-center gap-[21px]">
          <div class="flex w-[330px] max-w-full flex-col gap-2 text-white">
            <h1 class="text-shadow-[0px_0px_15px_rgba(255,255,255,0.7)] text-[30px] font-bold">
              {m.dialog_event_not_yet_started_title()}
            </h1>
            <p class="whitespace-pre-line text-base leading-6">
              {m.dialog_event_not_yet_started_description()}
            </p>
          </div>

          <div class="flex w-[330px] max-w-full items-center justify-between">
            <CountdownBox value={pad(countdown().day)} label={m.day()} />
            <span class="text-[25px] text-white">:</span>
            <CountdownBox value={pad(countdown().hours)} label={m.hour()} />
            <span class="text-[25px] text-white">:</span>
            <CountdownBox value={pad(countdown().minutes)} label={m.min()} />
          </div>
        </div>
      </div>
    </Show>
  )
}

function CountdownBox(props: { value: string; label: string }) {
  return (
    <div class="flex size-[88px] flex-col items-center justify-center rounded-[10px] bg-white/30 text-center text-white">
      <span class="text-[36px] font-bold leading-normal">{props.value}</span>
      <span class="text-base">{props.label}</span>
    </div>
  )
}
